import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { ErrorStateMatcher } from '@angular/material/core';
import { MatDialog } from '@angular/material/dialog';
import { MatInput } from '@angular/material/input';
import { BulkType, Week } from '@ngx-interval-data-grid';
import { TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import {
  BulkInput,
  InputTypeEnum,
  MinuteFrequencyOptionEnum,
  PeriodOption,
  PeriodOptionEnum,
  PeriodOptionValue,
  RegConfig,
  TabOption,
  TabOptionsEnum
} from '../../shared/models';
import { InternalService } from '../../shared/services/internal.service';
import { MixPanelService } from '../../shared/services/mixpanel.service';
import { BulkSelectionComponent } from '../dialogs/bulk-selection/bulk-selection.component';

@Component({
  selector: 'dr-customer-offers-ui-bulk-offers',
  templateUrl: './bulk-offers.component.html',
  styleUrls: ['./bulk-offers.component.scss']
})
export class BulkOffersComponent implements OnInit {
  @Input() maxDate!: moment.Moment;
  @Input() minDate!: moment.Moment;
  @Input() selectedTabOption!: TabOption;
  @Input() currentRegViewConfig!: RegConfig | null | undefined;
  @Input() allowZeroValue = false;
  @Input() isExternalUser = false;
  @Input() showOfferLock = false;

  @ViewChild(MatInput) matInputDirective?: MatInput;

  protected error = '';
  protected TabOptionsEnum = TabOptionsEnum;

  protected periodOptions: PeriodOption[] = [
    { value: PeriodOptionEnum.Week, label: this.translate.instant('COM.DIALOGS.BULK.ENTIRE_WEEK'), hidden: false },
    { value: PeriodOptionEnum.Custom, label: this.translate.instant('COM.DIALOGS.BULK.CUSTOM_RANGE'), hidden: false },
    { value: PeriodOptionEnum.Sunday, label: this.translate.instant('COM.LABELS.SUNDAY'), hidden: false },
    { value: PeriodOptionEnum.Monday, label: this.translate.instant('COM.LABELS.MONDAY'), hidden: false },
    { value: PeriodOptionEnum.Tuesday, label: this.translate.instant('COM.LABELS.TUESDAY'), hidden: false },
    { value: PeriodOptionEnum.Wednesday, label: this.translate.instant('COM.LABELS.WEDNESDAY'), hidden: false },
    { value: PeriodOptionEnum.Thursday, label: this.translate.instant('COM.LABELS.THURSDAY'), hidden: false },
    { value: PeriodOptionEnum.Friday, label: this.translate.instant('COM.LABELS.FRIDAY'), hidden: false },
    { value: PeriodOptionEnum.Saturday, label: this.translate.instant('COM.LABELS.SATURDAY'), hidden: false }
  ];
  protected selectedPeriod = this.periodOptions[0].value;
  protected bulkValue!: number | null;

  protected customErrorStateMatcher: ErrorStateMatcher = {
    isErrorState: () => this.isBulkValueInvalid
  };

  constructor(
    public dialog: MatDialog,
    private internalService: InternalService,
    private translate: TranslateService,
    private mixPanelService: MixPanelService
  ) {}

  ngOnInit(): void {
    const minuteFrequency = 1440 as MinuteFrequencyOptionEnum;
    if (this.currentRegViewConfig?.intervalFrequency === minuteFrequency) {
      const periodIndex = this.periodOptions.findIndex((period) => period.value == PeriodOptionEnum.Custom);
      this.periodOptions[periodIndex].hidden = true;
      this.periodOptions = this.periodOptions.filter((period) => period.hidden !== true);
    }
  }

  protected optionChange(value: PeriodOptionValue): void {
    if (value === PeriodOptionEnum.Custom) {
      this.bulkValue = null;
      setTimeout(() => {
        const activeElement = document.activeElement as HTMLElement; // Get the currently focused element
        activeElement.blur();
        this.openDialog();
      });
    }
  }

  private openDialog(): void {
    const dialogRef = this.dialog.open(BulkSelectionComponent, {
      data: {
        minuteFrequency: this.currentRegViewConfig?.intervalFrequency,
        maxBulkValue: this.currentRegViewConfig?.maximumValue,
        minBulkValue: this.currentRegViewConfig?.minimumValue,
        minDate: this.minDate,
        maxDate: this.maxDate,
        allowZeroValue: this.allowZeroValue,
        selectedTabOption: this.selectedTabOption,
        regConfig: this.currentRegViewConfig,
        isExternalUser: this.isExternalUser,
        showOfferLock: this.showOfferLock
      },
      width: 'calc(60rem * var(--app-rem-scale))',
      disableClose: false,
      hasBackdrop: true
    });

    dialogRef.afterClosed().subscribe((bulkOfferOutput: BulkInput) => {
      if (bulkOfferOutput) {
        console.error('bulkOfferOutput', bulkOfferOutput);
      }
      this.selectedPeriod = this.periodOptions[0].value;
      this.mixPanelService.clickBulkDay(this.selectedPeriod);
    });
  }

  protected apply(): void {
    if (this.bulkValue !== null) {
      this.mixPanelService.clickBulkApply();
      const output: BulkInput = {
        bulkType: this.selectedPeriod === 'Week' ? BulkType.WEEK : BulkType.DAY,
        inputType: InputTypeEnum.VALUE,
        value: this.bulkValue,
        week: this.selectedPeriod !== 'Week' && this.selectedPeriod !== 'Custom' ? (this.selectedPeriod as Week) : undefined,
        startDateAndTime: undefined,
        endDateAndTime: undefined
      };
      this.internalService.setBulkInputs(output);
    } else {
      this.error = this.translate.instant('COM.ERRORS.BULK_SELECTION.INVALID_VALUE') + this.currentRegViewConfig?.maximumValue;
    }
  }

  protected get isBulkValueInvalid(): boolean {
    if (this.bulkValue === 0 && this.allowZeroValue) return false;
    return (
      this.bulkValue === null ||
      this.bulkValue < (this.currentRegViewConfig?.minimumValue ?? 0) ||
      this.bulkValue > (this.currentRegViewConfig?.maximumValue ?? 0) ||
      this.bulkValue === 0
    );
  }

  protected onBulkValueChange(event: Event): void {
    if (isNaN((event.target as HTMLInputElement).valueAsNumber)) {
      this.bulkValue = null;
    } else {
      this.bulkValue = (event.target as HTMLInputElement).valueAsNumber;
    }

    if (this.matInputDirective) {
      this.matInputDirective.updateErrorState();
    }
  }
}
